/**
 * CustomParentDashboard.js — main parent dashboard route.
 *
 * Sprint 230 refresh — re-ordered by parent priority:
 *   1. Action Required strip (only renders when something needs attention)
 *   2. Teens grid (compact, 2-column on lg+, no dead whitespace)
 *   3. Completed Challenges Timeline (tighter)
 *   4. 180-Day Vault Clock + character NFTs
 *   5. Family invites + School sponsorship
 *   6. Family Commitment Charter — collapsible accordion at the bottom
 *
 * Visual direction: keep the warm cream + amber palette, but use rounded-2xl
 * corners + reduced padding + smaller type so the page feels lighter and
 * more "easy on the eye" without losing any information.
 */
 
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileText, Send, GraduationCap, Sparkles, Users, Loader2, Check,
  Lock, Clock, ExternalLink, ChevronDown, AlertTriangle,
} from 'lucide-react';
import { ParentShell } from '../components/parent/ParentShell';
import { apiClient } from '../api/client';

const CHARTER_PROMISES = [
  'Their practice sessions are private. You will not ask to read their conversations.',
  'You will support them unconditionally, without judgement or pressure.',
  'All rewards and digital assets they earn belong entirely to them.',
  'You are focused on their consistency and courage—not their grades or scores.',
];

// ── 0. Setup checklist — plan → parent signs → teen signs ───────────────
const SetupChecklist = ({ checklist }) => {
  if (!checklist) return null;
  const steps = [
    {
      done: checklist.plan_active,
      label: 'Choose your family plan',
      hint: 'Unlock the full 20-challenge journey',
      cta: !checklist.plan_active ? { label: 'View plans', href: '/parent-upgrade' } : null,
    },
    {
      done: checklist.parent_signed,
      label: 'Sign the Family Charter',
      hint: 'Your signature sends the Charter to your teen',
      cta: !checklist.parent_signed ? { label: 'Read & sign', href: '/charter' } : null,
    },
    {
      done: checklist.teen_signed,
      label: `${checklist.teen_first_name || 'Your teen'} signs their Charter`,
      hint: checklist.parent_signed && !checklist.teen_signed
        ? `We've emailed ${checklist.teen_first_name || 'them'} — no action needed from you`
        : 'Happens automatically after your signature',
      cta: null,
    },
  ];
  const doneCount = steps.filter(s => s.done).length;
  if (doneCount === steps.length) return null;

  return (
    <section
      className="rounded-2xl bg-white border border-gray-200 p-5"
      data-testid="parent-setup-checklist"
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold text-gray-900">Finish setting up</h2>
        <span className="text-xs font-semibold text-gray-500" data-testid="setup-checklist-progress">{doneCount} of {steps.length} done</span>
      </div>
      <ol className="space-y-2.5">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-3" data-testid={`setup-step-${i + 1}`}>
            <span className={`shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${
              s.done ? 'bg-emerald-500 text-white' : 'border-2 border-gray-300 text-transparent'
            }`}>
              <Check size={12} />
            </span>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${s.done ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{s.label}</p>
              {!s.done && <p className="text-xs text-gray-500">{s.hint}</p>}
            </div>
            {s.cta && (
              <a
                href={s.cta.href}
                className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full text-white"
                style={{ background: 'linear-gradient(135deg, #F5831F, #FFB073)' }}
                data-testid={`setup-step-${i + 1}-cta`}
              >
                {s.cta.label}
              </a>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
};

// ── 1. Action Required strip ────────────────────────────────────────────
const ActionRequiredStrip = ({ teens }) => {
  const items = useMemo(() => {
    const acc = [];
    teens.forEach((t) => {
      if (!t.status_aligned) {
        acc.push({
          kind: 'signature',
          label: `${t.teen_first_name || 'Teen'} — awaiting your signature`,
          severity: 'amber',
        });
      }
      (t.pending_meetups || []).forEach((m) => {
        acc.push({
          kind: 'meetup',
          label: `Live meet-up: ${m.where || 'Local meet'} · ${m.when || 'TBD'}`,
          severity: 'rose',
        });
      });
    });
    return acc;
  }, [teens]);

  if (items.length === 0) {
    return (
      <div
        className="rounded-2xl bg-emerald-50 border border-emerald-200 px-5 py-3 flex items-center gap-3"
        data-testid="action-required-strip-empty"
      >
        <Check size={18} className="text-emerald-600 shrink-0" />
        <p className="text-sm text-emerald-800">
          <strong>All clear.</strong> Nothing needs your attention right now — your teen is set up and practising.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4" data-testid="action-required-strip">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle size={16} className="text-amber-700" />
        <p className="text-xs font-bold uppercase tracking-wider text-amber-800">Action required</p>
      </div>
      <ul className="space-y-1.5">
        {items.map((i, idx) => (
          <li key={idx} className="text-sm text-amber-900 flex items-start gap-2" data-testid={`action-item-${idx}`}>
            <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${
              i.severity === 'rose' ? 'bg-rose-500' : 'bg-amber-600'
            }`} />
            {i.label}
          </li>
        ))}
      </ul>
    </div>
  );
};

// ── 2. Teens grid — compact, 2-column on lg+ ────────────────────────────
const TeenCard = ({ t }) => (
  <div
    className={`rounded-2xl bg-white border p-5 transition ${
      t.status_aligned
        ? 'border-emerald-200 shadow-[0_0_0_2px_rgba(16,185,129,0.12),0_12px_30px_-12px_rgba(16,185,129,0.25)]'
        : 'border-gray-200 shadow-sm'
    }`}
    data-testid={`teen-profile-${t.teen_id}`}
  >
    <div className="flex items-start gap-3">
      <div className="relative shrink-0">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold shadow overflow-hidden">
          {t.teen_avatar ? <img src={t.teen_avatar} alt={t.teen_name} className="w-full h-full object-cover" /> : (t.teen_first_name?.[0] || 'T')}
        </div>
        {t.status_aligned && (
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 border-2 border-white flex items-center justify-center">
            <Check size={10} className="text-white" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="text-base font-bold text-gray-900 truncate">{t.teen_first_name}</h2>
        <p className="text-xs text-gray-500 truncate">{t.teen_name}</p>
        <div className={`mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold ${
          t.status_aligned ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
        }`} data-testid={`status-pill-${t.teen_id}`}>
          <Sparkles size={10} />
          {t.status_aligned ? 'ALIGNED' : 'AWAITING SIGNATURE'}
        </div>
      </div>
    </div>

    <div className="mt-4 pt-4 border-t border-gray-100">
      <h3 className="text-xs font-bold uppercase tracking-wide text-gray-600 flex items-center gap-1.5 mb-2">
        <Users size={12} className="text-indigo-500" /> Peer Group & Meet-Up Radar
      </h3>
      {(!t.peer_group || t.peer_group.length === 0) ? (
        <p className="text-xs text-gray-400">No peer invites accepted yet.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {t.peer_group.slice(0, 6).map((p, i) => (
            <span key={i} className="px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-semibold inline-flex items-center gap-1">
              <Users size={9} /> {p.name}
            </span>
          ))}
          {t.peer_group.length > 6 && (
            <span className="px-2 py-1 rounded-full bg-gray-50 text-gray-500 text-[11px] font-semibold">
              +{t.peer_group.length - 6}
            </span>
          )}
        </div>
      )}
      {(t.pending_meetups || []).length > 0 && (
        <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3">
          <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700 mb-1">Live meet-up flag</p>
          {t.pending_meetups.map((m) => (
            <div key={m.meetup_id} className="text-xs text-amber-900 flex items-start gap-1.5 py-0.5">
              <Clock size={11} className="mt-0.5 shrink-0" />
              <span><strong>{m.where || 'Local meet'}</strong> · {m.when || 'TBD'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

const TeensGrid = ({ teens }) => {
  const names = teens.map(t => t.teen_first_name).filter(Boolean);
  const nameList = names.length > 1 ? `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}` : names[0];
  return (
    <section data-testid="parent-teens-grid">
      <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Your teens</h2>
      {teens.length === 0 ? (
        <div className="rounded-2xl bg-white border border-gray-200 p-6 text-center text-sm text-gray-500" data-testid="teens-empty-state">
          <p className="font-semibold text-gray-700 mb-1">No teens linked yet.</p>
          <p>Sign the Family Charter above — your signature sends it to your teen. The moment they co-sign, their name and progress appear right here.</p>
        </div>
      ) : (
        <>
          <div className="rounded-2xl bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 p-4 mb-4" data-testid="teens-mission-note">
            <p className="text-sm text-indigo-900 leading-relaxed">
              <strong>{nameList}</strong> {names.length > 1 ? 'are' : 'is'} building confidence and practising the
              daily-life conversational skills that will benefit {names.length > 1 ? 'them' : 'them'} today — and for
              years to come. Every practice session is an investment in their future.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {teens.map((t) => <TeenCard key={t.teen_id} t={t} />)}
          </div>
        </>
      )}
    </section>
  );
};

// ── 3. Completed Challenges Timeline ────────────────────────────────────
const ChallengesTimeline = ({ teens }) => {
  const allChallenges = useMemo(
    () => teens.flatMap(t => (t.completed_challenges || []).map(c => ({ ...c, teen: t.teen_first_name }))),
    [teens]
  );
  return (
    <section data-testid="parent-challenges" className="rounded-2xl bg-white border border-gray-200 p-5">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white">
          <Sparkles size={14} />
        </div>
        <h2 className="text-base font-bold text-gray-900">Completed Challenges Timeline</h2>
      </div>
      <p className="text-xs text-gray-500 mb-4">Every real-world challenge your teen has beaten.</p>
      {allChallenges.length === 0 ? (
        <p className="text-gray-400 text-xs py-6 text-center">No challenges completed yet. Their first run will land here.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {allChallenges.map((c, i) => (
            <li key={i} className="py-3 flex items-center justify-between gap-3" data-testid={`challenge-row-${i}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="shrink-0 w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 font-mono font-bold flex items-center justify-center text-xs">
                  {String(c.sequence ?? '·').padStart(2, '0')}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{c.title}</p>
                  <p className="text-[11px] text-gray-500">{c.teen}</p>
                </div>
              </div>
              <span className="shrink-0 text-xs font-semibold text-gray-600 tabular-nums">↔ {c.duration_label}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

// ── 4. Vault Clock ──────────────────────────────────────────────────────
const VaultClock = ({ globalVault, teens, onExecute }) => {
  const allNfts = useMemo(
    () => teens.flatMap(t => (t.vault?.nfts || []).map(n => ({ ...n, teen: t.teen_first_name }))),
    [teens]
  );
  return (
    <section
      className="rounded-2xl bg-gradient-to-br from-slate-900 to-indigo-950 text-white p-5 shadow-xl"
      data-testid="parent-vault"
    >
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-300/40 flex items-center justify-center">
            <Lock size={18} className="text-amber-300" />
          </div>
          <div>
            <h2 className="text-base font-bold">180-Day Vault Clock</h2>
            <p className="text-[11px] text-indigo-200">Crossmint trade execution mirror</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-black tracking-tight text-amber-300 font-mono" data-testid="vault-days-left">
            {String(globalVault.days_left).padStart(3, '0')}
          </div>
          <p className="text-[9px] uppercase tracking-[0.2em] text-indigo-200">days remaining</p>
        </div>
      </div>
      <p className="text-xs text-indigo-100/80 mb-4 leading-relaxed">{globalVault.matured_at_label}</p>
      {allNfts.length === 0 ? (
        <div className="text-center py-6 text-indigo-300/70 text-xs border border-dashed border-indigo-400/20 rounded-xl">
          Your teen hasn&apos;t minted any character NFTs yet.
        </div>
      ) : (
        <div className="space-y-2">
          {allNfts.map((n) => (
            <div key={n.meme_id} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/5 border border-white/10" data-testid={`vault-nft-${n.meme_id}`}>
              <div className="w-10 h-10 rounded-lg bg-indigo-700/40 overflow-hidden shrink-0">
                {n.image_url && <img src={n.image_url} alt={n.title} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{n.title}</p>
                <p className="text-[11px] text-indigo-300">{n.teen}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide ${
                n.teen_prefers === 'trade'
                  ? 'bg-rose-500/20 text-rose-200 border border-rose-400/40'
                  : 'bg-emerald-500/20 text-emerald-200 border border-emerald-400/40'
              }`}>
                {n.teen_prefers.toUpperCase()}
              </span>
              {n.teen_prefers === 'trade' && !n.executed_at && (
                <button onClick={() => onExecute(n.meme_id)}
                  className="ml-1 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-amber-950 font-bold text-[11px] inline-flex items-center gap-1 shadow"
                  data-testid={`run-crossmint-${n.meme_id}`}>
                  Run <ExternalLink size={10} />
                </button>
              )}
              {n.executed_at && (
                <span className="ml-1 text-[11px] text-emerald-300 inline-flex items-center gap-1"><Check size={11} /> Done</span>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// ── 5. Invites + Sponsor School ─────────────────────────────────────────
const InvitesAndSchool = () => {
  const [raw, setRaw] = useState('');
  const [school, setSchool] = useState('');
  const [city, setCity] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [schoolFeedback, setSchoolFeedback] = useState(null);

  const sendInvites = async () => {
    const contacts = raw.split(/[\n,;]/).map(s => s.trim()).filter(Boolean);
    if (!contacts.length) return;
    setBusy(true);
    try {
      const r = await apiClient.request('/api/parent/family-invites', { method: 'POST', body: JSON.stringify({ contacts }) });
      setFeedback({ ok: true, msg: `${r.queued} invitation${r.queued !== 1 ? 's' : ''} queued.${r.rejected.length ? ` ${r.rejected.length} rejected.` : ''}` });
      setRaw('');
    } catch (e) { setFeedback({ ok: false, msg: e?.message || 'Could not send invites.' }); }
    finally { setBusy(false); }
  };

  const submitSchool = async () => {
    if (!school.trim()) return;
    setBusy(true);
    try {
      await apiClient.request('/api/parent/sponsor-school', { method: 'POST', body: JSON.stringify({ school_name: school, city: city || null }) });
      setSchoolFeedback({ ok: true, msg: `${school} logged into the launch queue.` });
      setSchool(''); setCity('');
    } catch (e) { setSchoolFeedback({ ok: false, msg: e?.message || 'Could not submit.' }); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="parent-invites" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="rounded-2xl bg-white border border-gray-200 p-5">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-white">
            <Send size={14} />
          </div>
          <h2 className="text-sm font-bold text-gray-900">Invite other parents</h2>
        </div>
        <p className="text-xs text-gray-500 mb-3">Phone numbers or emails — one per line, or comma-separated.</p>
        <textarea rows={3} value={raw} onChange={e => setRaw(e.target.value)}
          placeholder={"sarah@example.com\n+1 555 123 0123\nlee@example.com"}
          className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none resize-none font-mono text-xs"
          data-testid="family-invites-input" />
        <button onClick={sendInvites} disabled={busy || !raw.trim()}
          className="mt-2 w-full py-2.5 bg-gradient-to-r from-sky-600 to-blue-700 text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 inline-flex items-center justify-center gap-1.5 text-sm"
          data-testid="family-invites-send">
          <Send size={14} /> Send Invites
        </button>
        {feedback && <p className={`mt-2 text-xs ${feedback.ok ? 'text-emerald-600' : 'text-red-600'}`} data-testid="family-invites-feedback">{feedback.msg}</p>}
      </div>

      <div className="rounded-2xl bg-gradient-to-br from-fuchsia-50 to-pink-50 border border-fuchsia-200 p-5">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-fuchsia-500 to-rose-500 flex items-center justify-center text-white">
            <GraduationCap size={14} />
          </div>
          <h2 className="text-sm font-bold text-gray-900">Sponsor / recommend a school</h2>
        </div>
        <p className="text-xs text-gray-600 mb-3">Nominate your teen&apos;s local school for a fully-gifted HiBFF rollout, funded by our community pool.</p>
        <input value={school} onChange={e => setSchool(e.target.value)} placeholder="School name"
          className="w-full px-3 py-2 border border-fuchsia-200 rounded-xl focus:border-fuchsia-500 focus:ring-1 focus:ring-fuchsia-500 outline-none text-sm"
          data-testid="sponsor-school-name" />
        <input value={city} onChange={e => setCity(e.target.value)} placeholder="City / Town (optional)"
          className="mt-2 w-full px-3 py-2 border border-fuchsia-200 rounded-xl focus:border-fuchsia-500 focus:ring-1 focus:ring-fuchsia-500 outline-none text-sm"
          data-testid="sponsor-school-city" />
        <button onClick={submitSchool} disabled={busy || !school.trim()}
          className="mt-2 w-full py-2.5 bg-gradient-to-r from-fuchsia-600 to-rose-600 text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 inline-flex items-center justify-center gap-1.5 text-sm"
          data-testid="sponsor-school-submit">
          <GraduationCap size={14} /> Nominate School
        </button>
        {schoolFeedback && <p className={`mt-2 text-xs ${schoolFeedback.ok ? 'text-emerald-700' : 'text-red-600'}`} data-testid="sponsor-school-feedback">{schoolFeedback.msg}</p>}
      </div>
    </section>
  );
};

// ── 6. Family Commitment Charter — collapsible accordion ────────────────
const CharterAccordion = () => {
  const [open, setOpen] = useState(false);
  return (
    <section data-testid="parent-charter"
      className="rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full p-4 flex items-center justify-between gap-3 hover:bg-amber-100/40 transition"
        data-testid="parent-charter-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2.5">
          <FileText size={16} className="text-amber-700" />
          <h2 className="text-sm font-bold text-amber-950" style={{ fontFamily: '"Playfair Display","Georgia",serif' }}>
            The Family Commitment Charter
          </h2>
          <span className="text-[10px] text-amber-700/80 font-semibold">— 4 promises</span>
        </div>
        <ChevronDown size={16} className={`text-amber-700 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1">
          <p className="text-[11px] text-amber-800/80 italic mb-4">Signed promises from Page 4 of the HiBFF Parent Compact — verbatim, immutable.</p>
          <ol className="space-y-3">
            {CHARTER_PROMISES.map((line, i) => (
              <li key={i} className="flex gap-3" data-testid={`charter-promise-${i + 1}`}>
                <span className="shrink-0 w-6 h-6 rounded-full bg-amber-700 text-white font-bold flex items-center justify-center text-xs">{i + 1}</span>
                <p className="text-amber-950 text-sm leading-relaxed" style={{ fontFamily: '"Playfair Display","Georgia",serif' }}>{line}</p>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
};

// =======================================================================
// MAIN
// =======================================================================
const CustomParentDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.request('/api/parent/custom-dashboard');
      setData(res);
    } catch (e) {
      setData({ parent: {}, teens: [], global_vault: { days_left: 180, matured_at_label: 'Locked until the 180-day countdown reaches zero to unlock and fully reveal character NFTs to your teen.' } });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const token = localStorage.getItem('hibff_auth_token');
    if (!token || typeof EventSource === 'undefined') return undefined;
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    let es;
    try {
      es = new EventSource(`${apiUrl}/api/teen/milestones/stream?token=${encodeURIComponent(token)}`);
      es.addEventListener('vault_choice_made', load);
    } catch (e) { /* */ }
    return () => { try { es && es.close(); } catch (e) { /* */ } };
  }, [load]);

  const handleExecuteCrossmint = async (memeId) => {
    try {
      const r = await apiClient.request(`/api/parent/trade-vault/${memeId}/execute`, { method: 'POST' });
      if (r?.redirect_url) window.open(r.redirect_url, '_blank', 'noopener');
      load();
    } catch (e) { alert(e?.message || 'Could not execute trade.'); }
  };

  return (
    <ParentShell activeId="dashboard" withBlind>
      {loading || !data ? (
        <div className="flex items-center justify-center py-32">
          <Loader2 className="animate-spin text-indigo-600" size={32} />
        </div>
      ) : (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-5" data-testid="custom-parent-dashboard">
          <SetupChecklist checklist={data.setup_checklist} />
          <ActionRequiredStrip teens={data.teens} />
          <TeensGrid teens={data.teens} />
          <ChallengesTimeline teens={data.teens} />
          <VaultClock globalVault={data.global_vault} teens={data.teens} onExecute={handleExecuteCrossmint} />
          <InvitesAndSchool />
          <CharterAccordion />
        </div>
      )}
    </ParentShell>
  );
};

export default CustomParentDashboard;
